import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, Checkbox, FormControlLabel, Card, CardContent, Button, Stack, Chip, useTheme, useMediaQuery, LinearProgress, Link } from '@mui/material';
import MainTemplate from './MainTemplate/MainTemplate';
import StepBackButton from './StepBackButton/StepBackButton';
import { useLocation, useNavigate } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import GavelTwoToneIcon from '@mui/icons-material/GavelTwoTone';
import ShieldTwoToneIcon from '@mui/icons-material/ShieldTwoTone';
import URL2 from '../config.js';
import { useSelector } from 'react-redux';
import { checkCodeofConduct } from '../utils/helper-functions.js';

const CodeofConduct = () => {
    const [accepted, setAccepted] = useState(false);
    const [feesPolicyAccepted, setFeesPolicyAccepted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [scrollProgress, setScrollProgress] = useState(0);

    const location = useLocation();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const scrollContainerRef = useRef(null);

    const college = location.state?.college;
    const SessionID = location.state?.SessionID;
    const { userIP } = useSelector((state) => state.auth);
    const parentHistory = useSelector((state) => state.parentInfo.parentHistory);

    const handleAgreeChange = (event) => {
        setAccepted(event.target.checked);
    };

    const handleFeesPolicyChange = (event) => {
        setFeesPolicyAccepted(event.target.checked);
    };

    const canContinue = accepted && feesPolicyAccepted && !loading;

    const feesPolicyLabel = (
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B' }}>
            I acknowledge that I have read the school's{' '}
            <Link
                href="/assets/School-Fees-Policy.pdf"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(event) => event.stopPropagation()}
                sx={{ fontWeight: 700 }}
            >
                fees policies and procedures
            </Link>
            .
        </Typography>
    );

    const handleSubmit = async () => {
        if (!canContinue) return;

        try {
            setLoading(true);
            const response = await URL2.post('/parent/accept-code-of-conduct', {
                ParentID: location.state?.ParentID,
                accepted: true,
                timestamp: new Date().toISOString(),
                userIP: userIP,
                family_name: (parentHistory?.family_name || '').trim(),
                email1: parentHistory?.contact?.email1?.trim() || '',
                email2: parentHistory?.contact?.email2?.trim() || '',
            });

            // Check if response is successful (status 200 or data.status 200 or data.success)
            if (response.status === 200 || response.data?.status === 200 || response.data?.success) {
                return navigate(`/parentinformation`, { state: location.state });
            } else {
                // Fallback navigate
                return navigate(`/parentinformation`, { state: location.state });
            }
        } catch (error) {
            console.error('Submission failed:', error);
            // Fallback navigate on error to prevent blocking user
            return navigate(`/parentinformation`, { state: location.state });
        } finally {
            setLoading(false);
        }
    };

    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        setScrolled(scrollTop > 30);
        const total = scrollHeight - clientHeight;
        if (total > 0) {
            const progress = Math.min(100, Math.round((scrollTop / total) * 100));
            setScrollProgress(progress);
        }
    };

    const policySections = [
        {
            title: '1. Introduction and Purpose',
            content: `Australian Islamic College (the College) highly values the important role played by parents and carers in their child’s formal education and recognises the responsibility and privilege it has in educating its students. The College places great emphasis on working in positive partnership and collaboration with parents and carers to ensure that students’ learning experiences are fully supported and optimised. The College also holds high expectations for conduct that reflects both Islamic values and standards of the school community. To ensure such cooperation and support, this Parent / Carer Code of Conduct (the Code) is intended as the foundation on which this partnership can be built in trust, with respect and in mutual understanding. Adhering to and supporting the general expectations set out in this Code will promote positive and productive relationships within the school community.`
        },
        {
            title: '2. Scope',
            content: `The College recognises that the role of a parent is fulfilled by a range of individuals within the variety of family structures that exist. The Code applies to all parents, step-parents, guardians, grandparents, extended family members and carers (collectively called parents / carers in this document) of Australian Islamic College students. This Code also applies across all campuses of the College. In developing the Code, the College recognises that parents / carers ultimately want the best for their children. However, the College also expects parents / carers to recognise that it must ultimately balance the interests of all the College’s stakeholders (including not only students and parents / carers, but also the College’s staff and their right to a safe working environment). This Code, therefore, is about promoting positive, supportive and respectful behaviours by parents / carers when visiting the College campus, when at school sporting events or excursions, and when interacting with College staff and other members of the school community, including face to face, phone, written and online communication. This Code does not attempt to provide a detailed and exhaustive list of what to do in every aspect of a parent / carer’s interactions with the College and the school community.`
        },
        {
            title: '3. Summary of Key Expectations',
            content: `In summary, the expectations of parents / carers expanded on in the following pages are as follows:\n1. Support the educational ethos and values of the College\n2. Behave respectfully towards members of our community\n3. Use technology and social media appropriately\n4. Be a responsible visitor and participant\n5. Raise grievances appropriately and productively`
        },
        {
            title: '4. Support the Educational Ethos and Values of the College',
            content: `Parents / carers are expected to support the educational ethos, core values, Islamic values and vision of the College, model appropriate behaviours for their children to learn from, and work with the College as it educates and provides pastoral support to all students.\nParents / carers can support the College and be positive role models by doing, for example, the following:\n▪ Comply with the College’s codes of conduct, directions, policies, procedures, rules and regulations, and ensure their children do the same.\n▪ Respect (and show to their children that they respect) that the College is inclusive and welcomes students from a variety of cultural and ethnic backgrounds, and with different needs.\n▪ Respect the College’s Islamic values, core values, vision and mission.\n▪ Respond to College communications (e.g. by completing forms and providing permissions in a timely manner) when requested to do so by the College.\n▪ Support the College’s approach to student behavioural concerns, which can include a range of outcomes (including those which are educational, pastoral or disciplinary in nature).\n▪ Encourage their children to actively participate in the life of the College, including in the classroom and the many sporting and extracurricular activities available (noting that some extracurricular activities will be compulsory).\n▪ Participate respectfully in the total life of the College through school-events such as Parent/Teacher nights, faith-based events (eg. Eid carnivals) and co-curricular activities.\n▪ Support the College’s commitment to providing an environment where each student can tap into their individual potential and attain academic excellence while integrating the principles of Islam into their everyday lives.\n▪ Be responsive to concerns raised by the College about their own child, including by being cooperative, providing information, and attending meetings when required.\n▪ Raise grievances directly with the College, and in a timely manner.\n▪ Keep the College informed about a child’s needs (including but not limited to their behavioural, educational, personal, and physical or mental health needs). This includes providing updated medical information, family developments and other like information as it becomes available. However, parents / carers need to also appreciate that while the College will take into account any new information, and comply with its legal obligations, the College cannot necessarily accommodate every need.\n▪ Keep the College informed about a child’s parenting arrangements, including any court orders that may be in place. However, parents / carers should not involve the College in parenting disputes, or expect the College to act as the go-between for estranged parents.\n▪ Recognise the damage that gossip can do within a school community, and avoid unconstructive commentary (including criticism, uninformed rumours or speculation) with other parents or students, including on social media.\n▪ Parents / carers are expected to be a role model for responsible and safe behaviours. This includes ensuring the health and safety of all members of our school community (including staff, students, parents and alumni) and the wider community. Parents / carers are expected to ensure that other individuals involved in their child’s life, such as other relatives and carers, also comply with this Code.`
        },
        {
            title: '5. Behave Respectfully Towards Members of Our School Community',
            content: `Parents / carers are required to support the College in its management of student behavioural concerns, including in relation to its disciplinary approach, and not undermine the College.\nThe College will not always be able to comment on specific details relating to other students for privacy reasons.\nParents / carers are expected to deal with College staff and all members of the school community respectfully at all times. “Respect” is intentionally a broad concept. This applies not only to words used, but also to tone and body language, and similar expectations are embedded in the College’s codes of conduct for staff and students.\nThe following is a non-exhaustive list of behaviours that are not respectful:\n▪ Rude or insulting behaviour, including passive-aggressive, intimidating or derogatory language.\n▪ Bullying, intimidation, discrimination, sexual harassment, victimisation and child abuse.\n▪ Actual or threatened aggression (verbal or non-verbal) or violence.\n▪ Behaviour that causes a risk to a person’s health and wellbeing.\n▪ Defamatory or disrespectful comments.\n▪ Gossip, rumour, and innuendo.\n▪ Raising one’s voice, or using offensive language or actions, while communicating.\n▪ Age-inappropriate language when communicating with or about children.\n▪ Vexatious complaints.\nIt is important that parents / carers show respect for staff and not publicly criticise them or seek to undermine their authority.\nThe College has a duty of care to protect all staff and for this reason any disrespectful, aggressive or abusive behaviour / communication will not be tolerated.`
        },
        {
            title: '6. Use Technology and Social Media Appropriately',
            content: `The expectations set out in this Code can also apply to the way a parent uses technology and behaves online.\nParents / carers are not permitted to take photos, videos or other recordings of a staff member or parent without their consent, or of a student without their parent’s consent, and not publish information (including personal details, contact information, images and recordings) concerning a staff member, parent, student or other member of the school community online without express consent.\nParents / carers are not permitted to contact students (other than their own) using any form of social media without the express consent of the student’s parents.\nParents / carers are not permitted to publish information which may bring the College (or any of its staff, students, parents and other members of the school community) into disrepute, or to create a website, blog, podcast, Facebook page, Instagram, X account or any other social media in the name of the College. In addition, no suggestion should be made that any such platform or material is operated or sanctioned by the College.\nParents / carers are not permitted to share contact details of other parents via social media unless they have consent. If consent is provided, parents / carers must not use those details for any reason other than the reason contact details were provided.\nThe name, logo or insignia of the College and the names of staff should not be used in the public arena, such as on social media, print or online publications, without the express permission of the Executive Principal.\nThis may include an image or recording which shows a student in school uniform, or a member of the school community at the College or at a school activity or event, behaving inappropriately.\nParents / carers should not discuss confidential or sensitive College matters, including in relation to grievances about a particular staff member or student, online.\nParents / carers groups on social media platforms (such as Facebook, WhatsApp or similar) are not authorised by the College unless written approval is provided and one of the administrators of the group is a nominated College staff member.`
        },
        {
            title: '7. Be a Responsible Visitor and Participant',
            content: `Parents / carers must respect the College’s risk-management procedures when visiting one of the College’s campuses. Parents / carers should immediately proceed to the relevant Reception upon arrival to sign in, and should only enter a classroom or other student environment when invited to do so by a staff member.\nThis requirement does not apply when visiting the College only to:\n▪ Attend an activity or event to which all members of the school community have been invited.\n▪ Visit the uniform shop.\n▪ Drop-off or collect a child from the College.\nWhen visiting the College, or attending College activities and events, parents should model appropriate and respectful behaviours, and uphold the College’s values. This includes:\n▪ Demonstrating good sporting conduct and fair play when attending sporting events and other College activities.\n▪ Complying with applicable occupation, student and workplace health and safety and risk-management procedures.\n▪ Complying with any reasonable directions given by the College’s staff.\n▪ Showing appropriate care and regard for the property of the College and others. Any damage should be promptly reported to the College.\n▪ Dressing appropriately for the occasion.\n▪ Not being under the influence of drugs or alcohol (and otherwise not engaging in the possession, sale or supply of the same at the College).\n▪ Behaving lawfully on College grounds whether at events hosted by or connected to the College, whether conducted on site or otherwise.\n▪ Ensuring that physical contact with students is appropriate given the age of, and relationship with, the student.\n▪ Respecting the College’s property and the property of other members of the school community (including staff, students and parents).\n▪ Respecting all road rules and speed restrictions and following instructions from College staff and security guards to assist with traffic management. This includes driving carefully and courteously, not parking in the Kiss & Drive area, and being courteous and respectful to other drivers, pedestrians, neighbours and their properties.`
        },
        {
            title: '8. Raise Grievances Appropriately and Productively',
            content: `The College is committed to the education and wellbeing of each student. It is therefore critical that parents are able to raise genuine grievances they may have about such matters in an appropriate, constructive and respectful forum.\nThe College’s grievance-management procedures are set out in a separate policy. This Code sets out how concerns and grievances may be raised with the College; who they should be raised with; and how the College will deal with these in a respectful and timely manner.\nParents / carers with grievances should consult the College’s Parent Carer Complaint Policy. However, in general:\n▪ Parents / carers should take care with volume, tone and vocabulary when communicating with another family’s child.\n▪ Parents / carers should not communicate with another student about an issue concerning their own child. In particular, parents should refrain from any attempt to discipline a student who is not their child, unless they are attempting to restrain a student from causing a risk to themselves or others.\n▪ Parents / carers should raise their grievances with their child’s teacher in the first instance. More serious concerns or grievances, including where a parent is dissatisfied with a teacher’s response to a grievance, may be raised with the appropriate member of the College as set out in the Grievances Policy (Community).\n▪ Parents / carers should arrange a face-to-face meeting to discuss their grievances, rather than relying on email or other written communications.\n▪ Parents / carers should appreciate that while the College is committed to dealing with their grievances in a timely manner, it will not always be practicable for staff to provide an immediate acknowledgement or response (particularly where a concern or grievance is sent by email).\n▪ Parents / carers should respect that the College employs experienced educators and other professionals who are well-trained in making academic, disciplinary, extracurricular, pastoral and wellbeing decisions every day. Please understand that while the College will always take into account the interests of the parent’s child, the College must ultimately make decisions that take into account the interests of all students (and others who may be affected by the College’s decisions).\n▪ Parents / carers should recognise that just as the College will seek to respect each student’s privacy, the College will also respect the privacy of other members of the school community. This means there are limits to what information the College will share with a parent when issues arise. This does not mean that the College is not taking an issue or situation seriously, or hiding information from a parent.\n▪ The College respects a parent / carer’s right to invoke any formal grievance-resolution procedures which may exist. However, parents /carers who refuse to engage in constructive processes that may resolve their grievances, or choose to publicly air their grievances about the College (and in particular about staff or students) on social media, are in clear breach of the Terms and Conditions of Enrolment.`
        },
        {
            title: '9. Student Attendance',
            content: `It is the responsibility of parents / carers to notify the College and provide written reasons for any student absences in accordance with the College’s Attendance Policy (a copy of which can be located on the College’s website).\nIt is expected that parents / carers will:\n▪ Provide evidence of lateness to allow students to enter the College grounds late.\n▪ Phone admin, send a note with their child, send an email or respond to a College absentees SMS and/or provide a medical certificate to account for student absences.\n▪ Complete a leave form if taking students overseas, and provide travel documents showing leaving and returning dates which is subject to Principal approval.\n▪ Explain absences to the satisfaction of the College (otherwise the absence will be deemed unapproved).\n▪ Provide evidence of an appointment if their child is leaving during school hours.\n▪ Abide to the College policy that students are not permitted to leave school early when they have a scheduled assessment task the next day.\n▪ Abide by the College’s Attendance Policy.`
        },
        {
            title: '10. Breaches of this Code',
            content: `The College acknowledges and applies the fundamental principles of procedural fairness and unbiased decision making when considering and investigating breaches of the Code.\nIn instances where there is a report of a breach or consistent breaches of the Code, the Executive Principal will have absolute discretion regarding how to best respond to the concerns raised.\nIf a parent / carer fails to observe the Code, the College will take appropriate action, which may include:\n▪ A request that the relevant conduct immediately cease.\n▪ Directing the parent / carer to immediately leave the College premises, including being escorted off College grounds by security or police.\n▪ A written warning.\n▪ A parent / carer (or another relevant person) being banned from the College grounds, either for a particular period of time or permanently.\n▪ A parent / carer (or another relevant person) being excluded from College activities or events.\n▪ A parent / carer (or another relevant person) having limited access to a College staff member/s\n▪ A requirement that a parent / carer (or another relevant person) only communicate with the College through a nominated College representative.\n▪ Terminating the enrolment of the student.\n\nPlease note, the Executive Principal (or their delegate) may in their absolute discretion terminate a student’s enrolment if the Executive Principal considers that a mutually beneficial relationship of trust and cooperation between a parent / carer and the College has broken down to the extent that it adversely impacts on that relationship.\nStaff are empowered to take steps to protect their own health and wellbeing. If they feel that a parent / carer is being inappropriate, they are encouraged to indicate this and ask that it stop. If it does not, or if a staff member feels that a parent / carer’s actions are posing a risk to their or someone else’s health and wellbeing, they are empowered to remove themselves from the situation. This may include immediately concluding a meeting or phone call, or demanding that a parent / carer immediately leave the College grounds (or a College activity or event).`
        },
        {
            title: '11. Further Information',
            content: `The College reserves the right to alter this Code at any time at its absolute discretion.\nReview Date: [May 2025]\nApproved by: [Executive Principal]\nBoard Sign Off Date: [May 2025]\nNext review: [May 2027]`
        }
    ];

    useEffect(() => {
        const checkConduct = async () => {
            if (location.state?.ParentID) {
                const res = await checkCodeofConduct(location.state.ParentID);
                if (res) {
                    navigate("/parentinformation", { state: location.state });
                }
            }
        };
        checkConduct();
    }, [location.state, navigate]);

    const content = (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                width: '100%',
                bgcolor: '#F8FAFC',
                px: { xs: 0, sm: 2, md: 3 },
                py: { xs: 1, sm: 3 },
                pb: { xs: 14, sm: 3 },
            }}
        >
            <Card
                sx={{
                    width: '100%',
                    maxWidth: { xs: '100%', lg: 1050 },
                    borderRadius: { xs: '12px', sm: '16px' },
                    boxShadow: { xs: '0 2px 10px rgba(0, 0, 0, 0.04)', sm: '0 10px 30px rgba(0, 0, 0, 0.05)' },
                    border: '1px solid #e2e8f0',
                    overflow: 'hidden',
                    bgcolor: '#FFFFFF',
                }}
            >
                <CardContent
                    sx={{
                        p: { xs: 1.5, sm: 3, md: 4 },
                        '&:last-child': { pb: { xs: 2, sm: 3, md: 4 } }
                    }}
                >
                    {/* Back to the introduction. The code of conduct is a gate, not a form, so there is
                        nothing here to lose by stepping back. */}
                    <Box sx={{ mb: { xs: 1, sm: 1.5 } }}>
                        <StepBackButton to="/introduction" label="Back" />
                    </Box>

                    {/* Collapsible / Shrinking Header Banner */}
                    <Box
                        sx={{
                            mb: { xs: 2, sm: 3 },
                            p: { xs: scrolled ? 1.25 : 2, sm: 3 },
                            borderRadius: { xs: 2.5, sm: 3 },
                            bgcolor: scrolled ? '#F8FAFC' : '#ECFDF5',
                            border: `1px solid ${scrolled ? '#E2E8F0' : '#A7F3D0'}`,
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: scrolled ? 'flex-start' : 'center',
                            textAlign: scrolled ? 'left' : 'center'
                        }}
                    >
                        {!scrolled && (
                            <Chip
                                icon={<ShieldTwoToneIcon style={{ color: '#059669', fontSize: 18 }} />}
                                label="Official School Policy"
                                sx={{
                                    bgcolor: '#FFFFFF',
                                    color: '#065F46',
                                    fontWeight: 600,
                                    fontSize: '12px',
                                    mb: 1,
                                    border: '1px solid #A7F3D0'
                                }}
                            />
                        )}

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', justifyContent: scrolled ? 'space-between' : 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <GavelTwoToneIcon sx={{ color: '#059669', fontSize: scrolled ? 22 : 28 }} />
                                <Typography
                                    variant="h5"
                                    component="h1"
                                    sx={{
                                        fontWeight: 800,
                                        color: '#0F172A',
                                        fontSize: scrolled ? { xs: '1.05rem', sm: '1.25rem' } : { xs: '1.3rem', sm: '1.75rem' },
                                        letterSpacing: '-0.01em',
                                        transition: 'font-size 0.3s ease'
                                    }}
                                >
                                    Parent Code of Conduct
                                </Typography>
                            </Box>
                            {scrolled && (
                                <Chip
                                    label={`${scrollProgress}% Read`}
                                    size="small"
                                    sx={{ bgcolor: '#059669', color: '#FFF', fontWeight: 700, fontSize: '11px' }}
                                />
                            )}
                        </Box>

                        {!scrolled && (
                            <Typography
                                variant="body2"
                                sx={{
                                    color: '#047857',
                                    fontSize: { xs: '13px', sm: '14.5px' },
                                    mt: 1,
                                    maxWidth: 680
                                }}
                            >
                                Please carefully read the following Code of Conduct policy before acknowledging your agreement below.
                            </Typography>
                        )}
                    </Box>

                    {/* Reading Progress Indicator Bar */}
                    <Box sx={{ width: '100%', mb: 2 }}>
                        <LinearProgress
                            variant="determinate"
                            value={scrollProgress}
                            sx={{
                                height: 4,
                                borderRadius: 2,
                                bgcolor: '#E2E8F0',
                                '& .MuiLinearProgress-bar': {
                                    bgcolor: '#059669',
                                    borderRadius: 2
                                }
                            }}
                        />
                    </Box>

                    {/* Policy Document Content Reader Box */}
                    <Box
                        ref={scrollContainerRef}
                        onScroll={handleScroll}
                        sx={{
                            p: { xs: 2, sm: 3 },
                            maxHeight: { xs: '55vh', sm: '50vh' },
                            overflowY: 'auto',
                            borderRadius: { xs: 2.5, sm: 3 },
                            bgcolor: '#FAFAFA',
                            border: '1px solid #E2E8F0',
                            boxShadow: 'inset 0 2px 6px rgba(0, 0, 0, 0.02)',
                            '&::-webkit-scrollbar': {
                                width: 6,
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: '#CBD5E1',
                                borderRadius: 3,
                            }
                        }}
                    >
                        {policySections.map((section, index) => (
                            <Box key={index} sx={{ mb: 3, '&:last-child': { mb: 0 } }}>
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        fontSize: { xs: '15px', sm: '16.5px' },
                                        fontWeight: 700,
                                        color: '#0F172A',
                                        mb: 1,
                                        pb: 0.5,
                                        borderBottom: '1px solid #E2E8F0'
                                    }}
                                >
                                    {section.title}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontSize: { xs: '13px', sm: '14px' },
                                        color: '#334155',
                                        lineHeight: 1.6,
                                        whiteSpace: 'pre-line'
                                    }}
                                >
                                    {section.content}
                                </Typography>
                            </Box>
                        ))}
                    </Box>

                    {/* Desktop Agreement Box */}
                    {!isMobile && (
                        <Box
                            sx={{
                                mt: 3,
                                pt: 2.5,
                                borderTop: '1px solid #E2E8F0',
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                                gap: 2
                            }}
                        >
                            <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={accepted}
                                            onChange={handleAgreeChange}
                                            sx={{
                                                color: '#059669',
                                                '&.Mui-checked': {
                                                    color: '#059669',
                                                },
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B' }}>
                                            I acknowledge that I have read and agree to the Parent Code of Conduct
                                        </Typography>
                                    }
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={feesPolicyAccepted}
                                            onChange={handleFeesPolicyChange}
                                            sx={{
                                                color: '#059669',
                                                '&.Mui-checked': {
                                                    color: '#059669',
                                                },
                                            }}
                                        />
                                    }
                                    label={feesPolicyLabel}
                                />
                            </Stack>
                            <Button
                                variant="contained"
                                size="large"
                                endIcon={<NavigateNextIcon />}
                                disabled={!canContinue}
                                onClick={handleSubmit}
                                sx={{
                                    bgcolor: '#059669',
                                    color: '#FFFFFF',
                                    fontWeight: 700,
                                    px: 4,
                                    py: 1.5,
                                    borderRadius: '12px',
                                    textTransform: 'none',
                                    fontSize: '15.5px',
                                    minWidth: 190,
                                    boxShadow: '0 4px 14px rgba(5, 150, 105, 0.35)',
                                    '&:hover': {
                                        bgcolor: '#047857',
                                    },
                                    '&.Mui-disabled': {
                                        bgcolor: '#CBD5E1',
                                        color: '#94A3B8'
                                    }
                                }}
                            >
                                Start Re-enrolment
                            </Button>
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* Compact Mobile Sticky Action Bar */}
            {isMobile && (
                <Box
                    sx={{
                        position: 'fixed',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        px: 2,
                        py: 1.25,
                        bgcolor: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(16px)',
                        borderTop: '1px solid rgba(226, 232, 240, 0.9)',
                        boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.08)',
                        zIndex: 1100,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1.5
                    }}
                >
                    <Stack spacing={0} sx={{ flex: 1, minWidth: 0 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={accepted}
                                    onChange={handleAgreeChange}
                                    size="small"
                                    sx={{
                                        color: '#059669',
                                        p: 0.5,
                                        '&.Mui-checked': {
                                            color: '#059669',
                                        },
                                    }}
                                />
                            }
                            label={
                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#0F172A', fontSize: '12px', lineHeight: 1.2 }}>
                                    I agree to Code of Conduct
                                </Typography>
                            }
                            sx={{ m: 0 }}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={feesPolicyAccepted}
                                    onChange={handleFeesPolicyChange}
                                    size="small"
                                    sx={{
                                        color: '#059669',
                                        p: 0.5,
                                        '&.Mui-checked': {
                                            color: '#059669',
                                        },
                                    }}
                                />
                            }
                            label={
                                <Typography variant="caption" sx={{ fontWeight: 600, color: '#0F172A', fontSize: '12px', lineHeight: 1.3 }}>
                                    I acknowledge that I have read the school's{' '}
                                    <Link
                                        href="/assets/School-Fees-Policy.pdf"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(event) => event.stopPropagation()}
                                        sx={{ fontWeight: 700 }}
                                    >
                                        fees policies and procedures
                                    </Link>
                                    .
                                </Typography>
                            }
                            sx={{ m: 0, alignItems: 'flex-start' }}
                        />
                    </Stack>
                    <Button
                        variant="contained"
                        size="medium"
                        endIcon={<NavigateNextIcon />}
                        disabled={!canContinue}
                        onClick={handleSubmit}
                        sx={{
                            bgcolor: '#059669',
                            color: '#FFFFFF',
                            fontWeight: 700,
                            px: 2.5,
                            py: 1,
                            borderRadius: '10px',
                            textTransform: 'none',
                            fontSize: '14px',
                            minHeight: '44px', // iOS 44px min touch target
                            whiteSpace: 'nowrap',
                            boxShadow: '0 4px 12px rgba(5, 150, 105, 0.3)',
                            '&:hover': {
                                bgcolor: '#047857',
                            },
                            '&.Mui-disabled': {
                                bgcolor: '#E2E8F0',
                                color: '#94A3B8'
                            }
                        }}
                    >
                        Continue
                    </Button>
                </Box>
            )}
        </Box>
    );

    return (
        <div>
            <MainTemplate college={`${college}`} content={content} SessionID={SessionID} />
        </div>
    );
};

export default CodeofConduct;
